import { fetcher } from '#lib/backendFetcher';
import { validateResponse } from '#lib/validation';

import { subscriptionSchema, productsSchema } from './billingSchemas';

/**
 * Fetches all available subscription products and validates them against the schema.
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export const fetchProducts = async () => {
	try {
		const data = await fetcher('/api/v1/platform/billing/products');
		const products = data?.products ?? [];
		if (products.length) validateResponse(productsSchema, products, 'products');
		return { data: products, error: null };
	} catch (err) {
		return { data: null, error: err };
	}
};

/**
 * Fetches all available prices for the subscription products.
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export const fetchPrices = async () => {
	try {
		const data = await fetcher('/api/v1/platform/billing/prices');
		const prices = data?.prices ?? [];
		return { data: prices, error: null };
	} catch (err) {
		return { data: null, error: err };
	}
};

/**
 * Fetches the active subscription of the logged-in user and validates it.
 * @returns {Promise<{data: Object|null, error: null}>}
 */
export const fetchUserSubscription = async () => {
	const data = await fetcher('/api/v1/platform/billing/subscription');
	const subscription = data?.subscription ?? null;
	validateResponse(subscriptionSchema, subscription, 'subscription');
	return { data: subscription, error: null };
};

/**
 * Creates a Stripe checkout session for a plan price and optionally passes a Rewardful referral ID.
 * The session is always a subscription checkout; one-time purchases have their own endpoints.
 * @param {string} priceId - Stripe Price ID for the selected plan
 * @returns {Promise<{data: {sessionId: string, url: string}, error: null}>}
 */
export const createCheckoutSession = async (priceId) => {
	const payload = { priceId };

	// Only include referralId if it exists (the backend rejects an empty value).
	if (window.Rewardful?.referral) {
		payload.referralId = window.Rewardful.referral;
	}

	const data = await fetcher('/api/v1/platform/billing/create-checkout-session', {
		method: 'POST',
		body: payload,
	});

	return { data, error: null };
};

/**
 * Creates a Stripe Customer Portal session for subscription management.
 * @returns {Promise<{data: Object, error: null}>}
 */
export const createPortalSession = async () => {
	const data = await fetcher('/api/v1/platform/billing/create-portal-session', {
		method: 'POST',
	});

	return { data, error: null };
};

/**
 * Changes the active subscription to a new plan. An upgrade applies at once; a downgrade takes
 * effect at the end of the paid period and reports when that is via `effectiveAt`.
 * @param {string} newPriceId - Stripe Price ID of the new plan
 * @returns {Promise<{data: {success: boolean, isUpgrade: boolean, effectiveAt: string|null}, error: null}>}
 */
export const changeSubscription = async (newPriceId) => {
	const data = await fetcher('/api/v1/platform/billing/change-subscription', {
		method: 'POST',
		body: { newPriceId },
	});

	return { data, error: null };
};

/**
 * Adds an additional seat to the current subscription.
 * @returns {Promise<{data: Object, error: null}>}
 */
export const addSeat = async () => {
	const data = await fetcher('/api/v1/platform/billing/add-seat', {
		method: 'POST',
	});

	return { data, error: null };
};

/**
 * Starts (or activates) an add-on on the org's existing subscription. Begins a 14-day trial if the
 * org has never used it, otherwise activates it as paid. Generic across add-ons (e.g. 'crm').
 * @param {string} addonKey
 * @returns {Promise<{data: Object, error: null}>}
 */
export const startAddon = async (addonKey) => {
	const data = await fetcher(`/api/v1/platform/billing/addon/${addonKey}`, {
		method: 'POST',
	});

	return { data, error: null };
};

/**
 * Cancels an add-on. A trial ends at once; a paid add-on keeps running until the period the
 * customer paid for is over, which `canceledImmediately` distinguishes.
 * @param {string} addonKey
 * @returns {Promise<{data: {success: boolean, canceledImmediately: boolean, cancelAtPeriodEnd: boolean}, error: null}>}
 */
export const cancelAddon = async (addonKey) => {
	const data = await fetcher(`/api/v1/platform/billing/addon/${addonKey}`, {
		method: 'DELETE',
	});

	return { data, error: null };
};

const postSubscriptionLifecycle = async (action, body) => {
	const data = await fetcher(`/api/v1/platform/billing/${action}`, { method: 'POST', body });
	validateResponse(subscriptionSchema, data?.subscription, 'subscription');
	return { data, error: null };
};

/**
 * Cancels the subscription at the end of the paid period and records why, for the retention follow-up.
 * @param {Object} params
 * @param {string} params.reason - cancellation reason enum value (e.g. 'no_time')
 * @param {string|null} params.comment - free text, max 500 characters
 * @param {'fullservice'|'call'|'pause'|null} params.offerShown - retention offer the customer saw first, null when none ran
 * @returns {Promise<{data: {subscription: Object}, error: null}>}
 */
export const cancelSubscription = ({ reason, comment, offerShown }) =>
	postSubscriptionLifecycle('cancel-subscription', { reason, comment, offerShown });

/**
 * Withdraws a pending cancellation so the subscription renews again.
 * @returns {Promise<{data: {subscription: Object}, error: null}>}
 */
export const revertCancellation = () => postSubscriptionLifecycle('revert-cancellation');

/**
 * Pauses the subscription from the end of the paid period for 1–3 months (no charges, read-only access).
 * @param {Object} params
 * @param {1|2|3} params.months
 * @param {string|null} params.reason - cancellation reason enum value, null when pausing an already cancelled subscription
 * @param {string|null} params.comment - free text, max 500 characters
 * @param {'fullservice'|'call'|'pause'|null} params.offerShown - retention offer the customer saw first, null when none ran
 * @returns {Promise<{data: {subscription: Object}, error: null}>}
 */
export const pauseSubscription = ({ months, reason, comment, offerShown }) =>
	postSubscriptionLifecycle('pause-subscription', { months, reason, comment, offerShown });

/**
 * Records that the customer submitted the reason step of the cancel flow, so an abandoned flow is
 * visible for proactive retention follow-up. Fire-and-forget: callers must not block the flow on it.
 * @param {Object} params
 * @param {string} params.reason - cancellation reason enum value (e.g. 'no_time')
 * @param {string|null} params.comment - free text, max 500 characters
 * @param {'fullservice'|'call'|'pause'|null} params.offerShown - offer the flow shows next, null when there is none
 * @returns {Promise<{success: boolean}>}
 */
export const recordRetentionFlowEvent = ({ reason, comment, offerShown }) =>
	fetcher('/api/v1/platform/billing/retention-flow-event', { method: 'POST', body: { reason, comment, offerShown } });

/**
 * Ends a scheduled or running pause immediately; billing and full access resume at once.
 * @returns {Promise<{data: {subscription: Object}, error: null}>}
 */
export const resumeSubscription = () => postSubscriptionLifecycle('resume-subscription');
